// @ts-nocheck
import * as R from "ramda"
import moment from "moment"
import { Field } from "formik"
import { RadioBox } from "@components/radio-box"
import { ColouredText } from "@components/vehicle-row"
import { capitalize } from "@components/delivery-mode"
import { citiesData } from "@carpaddy-fixtures/cities-data"
import { v4 as uuid } from "uuid"
import { orderAwaitingStatus } from "@carpaddy-fixtures/constants"
import { orderStages } from "@carpaddy-fixtures/index"

import {
  vehicleKeys,
  notDeliveredDocs,
} from "@carpaddy-fixtures/vehicle-papers"
import { encrypt } from "@app/helpers"
import { buildUniversalAppLink } from "@features/payments/helpers"

const docsThatNeedQuantityForCalculation = ["vl", "hp"]

export const orderStagesMap = {
  [orderStages.PROCESS]: "Started",
  [orderStages.WAITING]: "Processed",
  [orderStages.HOLD]: "On Hold",
  [orderStages.DELIVER]: "On the way",
  [orderStages.COMPLETE]: "Delivered",
  [orderStages.FAILED]: "Failed",
}

export const TWO_WEEKS_TO_EXPIRY = 15

export const DOCUMENT_TITLE = {
  renewal: "Paper Renewal",
  register: "New vehicle registration",
  ownership: "Change of Ownership",
  driverslicense: "Driver's Licence",
}

export const expiryMoreThanThirtyDays = (values: {
  day: string
  month: string
  year: string
}) => {
  const expiryDate = `${values.day}-${values.month}-${values.year}`
  const date = moment(expiryDate, "DD-MM-YYYY")
  const today = moment()
  return date.diff(today, "days")
}

export const formatSendProcessingEmailFn = ({
  to,
  title,
  label,
  stage,
  urls,
}) => {
  let message = `<p><strong>${title}-${label}</strong></p>`

  // Format URLs as a list if they exist
  if (urls && urls.length > 0) {
    message += `<p>Below documents were uploaded, please open the app to view/download them:</p><ul>`
    urls.forEach((urlItem: any) => {
      // we are not using urls here on purpose, users can login to their app
      message += `<li><a href=${process.env.REACT_APP_URL}/dashboard target="_blank">${urlItem.title}</a></li>`
    })
    message += `</ul>`
  }

  const orderStatus = orderStagesMap[stage] || "Updates"

  return {
    sender_name: "CarPaddy.com",
    sender_email: "support@carpaddy.com",
    to,
    title: `Order ${orderStatus}`,
    message,
    stage,
  }
}

export const formatSendProcessingNotificationFn = ({
  orderTitle,
  orderLabel,
  stage,
  urls,
}) => {
  const orderStatus = orderStagesMap[stage] || "Updates"
  const statusText =
    orderStatus === "Updates" ? "updated" : orderStatus.toLowerCase()

  return {
    title: `Order ${orderStatus}`,
    body: `Your order: ${orderTitle} - ${orderLabel} is ${statusText}. ${
      urls.length > 0
        ? `You can check your uploaded documents in the app.`
        : "You may check your order status in the app for details."
    }`,
    data: {
      url: buildUniversalAppLink("orders"),
    },
  }
}

export const formatSendDocUploadedEmailFn = ({
  to,
  title,
  label,
  urls,
  stage,
}) => {
  let message = `<p><strong>${title}-${label}</strong></p>`

  // Format URLs as a list if they exist
  if (urls && urls.length > 0) {
    message += `<p>Below documents were uploaded for your order, please open the app to view/download them:</p><ul>`
    urls.forEach((urlItem: any) => {
      // we are not using urls here on purpose, users can login to their app
      message += `<li><a href=${process.env.REACT_APP_URL}/dashboard target="_blank">${urlItem.title}</a></li>`
    })
    message += `</ul>`
  }

  return {
    sender_name: "CarPaddy.com",
    sender_email: "support@carpaddy.com",
    to,
    title: `New Documents Uploaded`,
    message,
    stage,
  }
}

export const formatSendDocUploadedNotificationFn = ({
  orderTitle,
  orderLabel,
  urls,
}) => {
  return {
    title: `New Documents Uploaded`,
    body: `You have new documents uploaded for your order: ${orderTitle} - ${orderLabel}, click on your vehicle widget menu, to see them.`,
    data: {
      url: buildUniversalAppLink("orders"),
    },
  }
}

export const calculatePaperQty = (expiryDate: string) => {
  if (!expiryDate) return 1

  let result
  const diff = daysToRenewal(expiryDate)
  result = Math.ceil(Math.abs(diff) / 365)
  return result < 1 ? 1 : result
}

export const formatDriversLicenseInfo = (
  _data: any,
  processingCity,
  citiesData,
) => {
  const { deliveryMode, deliveryCity, type, day, month, year, processing } = _data

  let licenseToRenew = {}
  const licenseIdToUse = _data?.licenseid || uuid()
  // const licenseid = uuid()

  // if it is a renewal process
  if (deliveryMode && deliveryCity) {
    const licenseDocsInfo = citiesData[processingCity.value].licenses[type]
    // format the docs and add them to an array as well as total of all prices
    const documents = [
      {
        key: "dl",
        price: licenseDocsInfo.price.price,
        cost: licenseDocsInfo.price.cost,
        qty: 1,
        expiryDate: `${year}-${month}-${day}`,
        daysToRenewal: daysToRenewal(`${year}-${month}-${day}`),
      },
    ]

    const fees = [
      {
        name: "documents",
        amount: licenseDocsInfo.price.price,
        cost: licenseDocsInfo.price.cost,
      },
    ]

    // Add processing fee document if selected
    if (processing && processing.price > 0) {
      documents.push({
        key: "processing",
        price: processing.price,
        cost: processing.cost,
        qty: processing.qty,
        expiryDate: null,
        daysToRenewal: null,
      })

      fees.push({
        name: "processing",
        amount: processing.price,
        cost: processing.cost,
      })
    }

    const docs = {
      documents,
      fees,
    }

    // get delivery price from citiesData
    const INTERCITY_DELIVERY_FEE = citiesData[
      processingCity.value
    ].deliveryPrices.filter((doc) => doc.value === "intercity")[0].amount

    // format prices for delivery and addtoDb
    // if the dliveryCity is not one of the available cities, delivery fee is intercity
    let delivery = 0
    if (
      !R.has(deliveryCity?.value)(citiesData) &&
      deliveryMode === "physical"
    ) {
      delivery = INTERCITY_DELIVERY_FEE
    }

    // add delivery fees into the docs.fees array
    docs.fees.push({ name: "delivery", amount: delivery })

    licenseToRenew = R.mergeAll([
      { licenseid: licenseIdToUse },
      // { addMethod: "online" },
      { deleted: false },
      { fees: docs.fees },
      { licenseNo: _data.licenseNo.toUpperCase() },
      // { name: _data.name },
      { documents: docs.documents },
      // { type: _data.type },
      { title: licenseDocsInfo.name },
      { label: _data.licenseNo.toUpperCase() },
      { deliveryMode },
      { deliveryQty: 1 },
      { deliveryCity: deliveryCity.value },
      { processingCity: processingCity.value },
      { notes: _data.notes },
      { status: [{ type: "approval", data: orderAwaitingStatus.BIOMETRIC }] },
      { uploads: _data?.uploads || [] },
    ])
  }

  const licenseToSave: any = R.mergeAll([
    { licenseid: licenseIdToUse },
    { addMethod: "online" },
    { deleted: false },
    { licenseNo: _data.licenseNo.toUpperCase() },
    { name: _data.name },
    { type: _data.type },
    { nin: encrypt(_data?.nin || "", { strict: true }) },
    { ninName: encrypt(_data?.ninName || "", { strict: true }) },
    {
      documents: [
        {
          key: "dl",
          expiryDate: `${year}-${month}-${day}`,
          upload: "",
        },
      ],
    },
  ])

  return { licenseToSave, licenseToRenew }
}

export const addOwnershipDocsToProcess = (
  citiesData,
  city,
  typeOfDoc,
  typeOfPlate,
  category,
  vehicleDocuments,
) => {
  const cityDocs = citiesData[city].docs(typeOfDoc, typeOfPlate)

  const startingDocsForOwnershipArray = [
    "coc",
    ...cityDocs.filter((doc) => doc.key === "coc")[0].connected,
    "ins",
  ]

  const startingDocs = cityDocs.reduce(
    (acc: any, doc: any) => {
      const docExist = vehicleDocuments.filter((vdoc) => vdoc.key === doc.key)
      const expiryDate = docExist.length ? docExist[0].expiryDate : null

      if (startingDocsForOwnershipArray.includes(doc.key)) {
        acc[doc.key] = {
          expiryDate: expiryDate,
          disabled: doc.key === "ins" ? false : true,
          qty: docsThatNeedQuantityForCalculation.includes(doc.key)
            ? calculatePaperQty(expiryDate)
            : 1,
          price: doc.price[category].price,
          cost: doc.price[category].cost,
        }
        acc.vehicle.documents.push({
          key: doc.key,
          expiryDate: expiryDate,
          disabled: doc.key === "ins" ? false : true,
          qty: 1,
          price: doc.price[category].price,
          cost: doc.price[category].cost,
        })
      }
      return acc
    },
    { vehicle: { documents: [] } },
  )

  return startingDocs
}

export const addRegistrationDocsToProcess = (
  citiesData,
  city,
  typeOfDoc,
  typeOfPlate,
  category,
) => {
  const cityDocs = citiesData[city].docs(typeOfDoc, typeOfPlate)

  const startingDocsForRegistrationArray = [
    "np",
    ...cityDocs.filter((doc) => doc.key === "np")[0].connected,
    "ins",
  ]

  const startingDocs = cityDocs.reduce(
    (acc: any, doc: any) => {
      if (startingDocsForRegistrationArray.includes(doc.key)) {
        acc[doc.key] = {
          expiryDate: null,
          disabled: doc.key === "ins" ? false : true,
          qty: 1,
          price: doc.price[category].price,
          cost: doc.price[category].cost,
        }
        acc.vehicle.documents.push({
          key: doc.key,
          expiryDate: null,
          disabled: doc.key === "ins" ? false : true,
          qty: 1,
          price: doc.price[category].price,
          cost: doc.price[category].cost,
        })
      }
      return acc
    },
    { vehicle: { documents: [] } },
  )

  return startingDocs
}

export const formatProcessingInfo = (_data: any, uploads: any[]) => {
  const needToAddToLagosDatabase =
    _data.needToAddToLagosDatabase === "false" ? false : true

  // get doc price from citiesDtata
  const ADD_TO_LAGOS_DB_FEE = needToAddToLagosDatabase
    ? citiesData.lagos
        .docs(_data.vehicle.typeOfDoc)
        .filter((doc) => doc.key === "addToDb")[0].price[_data.vehicle.category]
        .price
    : 0

  const {
    deliveryMode,
    deliveryInfo,
    deliveryCity,
    processingCity,
    deliveryQty,
  } = _data

  // get delivery price from citiesDtata
  const INTERCITY_DELIVERY_FEE = citiesData[
    processingCity.value
  ].deliveryPrices.filter((doc) => doc.value === "intercity")[0].amount

  //get the docs for the processing city
  const citiesDocs = citiesData[processingCity.value].docs(
    _data?.vehicle?.typeOfDoc,
    _data?.vehicle?.typeOfPlate,
  )

  // format the docs and add them to an array as well as total of all prices
  let total = 0
  let cost = 0
  // loop through the citiesDocs and add the ones that have a price to the docs array
  const docs = citiesDocs.reduce(
    (acc: any, curr: any) => {
      if (R.has(curr.key)(_data) && _data[curr.key]?.price > 0) {
        const docData = {
          key: curr.key,
          price: _data[curr.key].price,
          cost: _data[curr.key].cost,
          qty: _data[curr.key].qty,
          daysToRenewal: _data[curr.key].daysToRenewal,
        }
        // add vis details
        if (_data?.vis_id && curr.key === "rw") {
          docData.vis_id = _data.vis_id
        }
        acc.documents.push(docData)

        const qty = _data[curr.key].qty || 1
        total += _data[curr.key].price * qty
        cost += _data[curr.key].cost ? _data[curr.key].cost * qty : 0
        acc.fees[0] = { name: "documents", amount: total, cost }
      }

      return acc
    },
    { documents: [], fees: [] },
  )

  // format prices for delivery and addtoDb
  let delivery = 0
  if (
    deliveryCity.value === processingCity.value &&
    deliveryMode === "physical"
  ) {
    delivery = 0
  }
  if (
    deliveryCity.value !== processingCity.value &&
    deliveryMode === "physical"
  ) {
    delivery = INTERCITY_DELIVERY_FEE
  }

  // add delivery fees into the docs.fees array
  docs.fees.push(
    { name: "delivery", amount: delivery },
    { name: "addToDb", amount: ADD_TO_LAGOS_DB_FEE },
  )

  const vehicleData: any = R.mergeAll([
    { regNo: _data.vehicle.regNo || "" },
    { uid: _data.uid },
    { vid: _data?.vehicle?.vid || _data?.vid },
    { title: _data.title },
    { label: _data.vehicle.label || _data.label },
    { documents: docs.documents },
    { fees: docs.fees },
    { deliveryMode },
    { deliveryInfo },
    { deliveryQty: deliveryQty || 1 },
    { deliveryCity: deliveryCity.value },
    { processingCity: processingCity.value },
    { needToAddToLagosDatabase },
    { deleted: false },
    { notes: _data?.notes },
    { uploads: uploads || [] },
  ])

  return vehicleData
}

export const formatComprehensiveInsuranceInfo = (
  _data: any,
  uploads: any[],
) => {
  const { deliveryMode, deliveryCity, processingCity } = _data

  const PROCESSING_FEE_PRICE = citiesData[processingCity.value]
    .docs("private", "regular")
    .filter((doc) => doc.key === "processing")[0].price[1].price
  // get delivery price from citiesDtata
  const INTERCITY_DELIVERY_FEE = citiesData[
    processingCity.value
  ].deliveryPrices.filter((doc) => doc.value === "intercity")[0].amount

  // format the docs and add them to an array as well as total of all prices
  const documents = [
    {
      key: "ci",
      price: _data.price,
      cost: _data.cost,
      qty: 1,
      expiryDate: `${_data.expiryDate.year}-${_data.expiryDate.month}-${_data.expiryDate.day}`,
      daysToRenewal: _data.daysToRenewal,
      insurer: _data.insurer,
      product: `${capitalize(_data.insurer)} - ${capitalize(_data.product)}`,
      name: _data.name,
      vehiclePrice: _data.vehiclePrice,
      policyHolderName: _data?.policyHolderName ?? "",
      dateOfBirth: _data?.dateOfBirth ?? "",
      residentialAddress: _data?.residentialAddress,
      carUsePurpose: _data?.carUsePurpose,
      otherUse: _data?.otherUse ?? "",
      exclusiveDriver: _data?.exclusiveDriver ?? "",
      otherDriver: _data?.otherDriver ?? "",
      otherDriverRelationship: _data?.otherDriverRelationship ?? "",
      nin: _data?.nin ?? "",
    },
    {
      key: "processing",
      qty: 1,
      cost: 0,
      price: PROCESSING_FEE_PRICE,
      daysToRenewal: null,
    },
  ]

  const fees = [
    {
      name: "documents",
      amount: _data.price, // dnt add processing fee tot toal for com pinsurance + PROCESSING_FEE_PRICE,
      cost: _data.cost,
    },
  ]

  const docs = { documents, fees }

  // format prices for delivery and addtoDb
  let delivery = 0
  if (
    deliveryCity.value === processingCity.value &&
    deliveryMode === "physical"
  ) {
    delivery = 0
  }
  if (
    deliveryCity.value !== processingCity.value &&
    deliveryMode === "physical"
  ) {
    delivery = INTERCITY_DELIVERY_FEE
  }

  // add delivery fees into the docs.fees array
  docs.fees.push({ name: "delivery", amount: delivery })

  const vehicle = _data.vehicle?.uid ? _data.vehicle : _data.vehicleData

  const compInsuranceData: any = R.mergeAll([
    { regNo: vehicle?.regNo || "" },
    { uid: vehicle?.uid || _data?.uid },
    { vid: vehicle?.vid },
    { title: _data.title },
    { identifier: _data?.identifier },
    {
      label: `${vehicle?.make} ${vehicle?.model} (${vehicle?.regNo})`,
    },
    { documents: docs.documents },
    { fees: docs.fees },
    { deliveryMode },
    { deliveryQty: 1 },
    { deliveryCity: deliveryCity.value },
    { processingCity: processingCity.value },
    { deleted: false },
    { notes: _data?.notes },
    { uploads: uploads || [] },
  ])

  return compInsuranceData
}

export const formatVehicleTrackingInfo = (_data: any) => {
  const { deliveryMode, deliveryCity, processingCity } = _data

  const vehicle = _data.vehicle?.uid
    ? _data.vehicle
    : _data.vehicleData || _data.vehicle

  const documents = [
    {
      key: "vt",
      price: _data.price,
      qty: 1,
      planId: _data.planId,
      product: _data.product,
      email: _data.email,
      name: _data.name,
      installationAddress: _data.installationAddress,
      installationBusstop: _data.installationBusstop,
    },
  ]

  const fees = [
    {
      name: "documents",
      amount: _data.price,
      cost: _data.price,
    },
  ]

  // all delivery for vehicle tracking will always be zero for now
  let delivery = 0

  fees.push({ name: "delivery", amount: delivery })

  const vehicleTrackingData: any = R.mergeAll([
    { regNo: vehicle?.regNo || "" },
    { uid: vehicle?.uid || _data?.uid },
    { vid: vehicle?.vid },
    { title: _data.title },
    { identifier: _data?.identifier },
    {
      label: vehicle
        ? `${vehicle.make || ""} ${vehicle.model || ""} (${vehicle.regNo || ""})`
        : "Vehicle Tracking",
    },
    { documents },
    { fees },
    { deliveryMode },
    { deliveryQty: 1 },
    { deliveryCity: deliveryCity.value },
    { processingCity: processingCity.value },
    { deleted: false },
    { notes: _data?.notes || null },
    { uploads: [] },
  ])

  return vehicleTrackingData
}

export const formatExtraPaymentsInfo = (_data: any) => {
  const vehicleData: any = R.mergeAll([
    { regNo: "" },
    { uid: _data.uid },
    { vid: null },
    { title: "Extra Order Payments" },
    { label: _data.description },
    { documents: [] },
    {
      fees: [
        {
          cost: Number(_data.amount),
          name: "extra",
          amount: Number(_data.amount),
        },
      ],
    },
    { deliveryMode: "none" },
    { deliveryInfo: { email: _data.email } },
    { deliveryQty: 1 },
    { deliveryCity: null },
    { processingCity: null },
    { needToAddToLagosDatabase: false },
    { deleted: false },
    { notes: null },
    { uploads: [] },
  ])

  return vehicleData
}

export const daysToRenewal = (expiryDate: string) => {
  const today = moment()
  const expiry = moment(expiryDate, "YYYY-MM-DD")
  return expiry.diff(today, "days")
}

// sorts documents by existing document on car, if any, then filters out any document not needed
// filter with the process key/value pair and also price in the citiesData

export const sortDocsByExisting = (
  documents: any,
  cityDocs: any,
  process: string,
  startingDocs: any[],
) => {
  // filter complimentary docs/process that exists
  const otherDocsOrProcesses = documents.reduce((acc: any, doc: any) => {
    if ((doc.key === "ci" || doc.key === "vt") && doc.expiryDate) {
      acc[doc.key] = doc
    }
    return acc
  }, {})

  // filter out doucments that are not needed for process
  const filteredCityDocs = cityDocs.filter((doc) => {
    if (doc.process.includes(process)) {
      return true
    }
    return false
  })

  const filtered = [
    ...R.filter(
      R.propSatisfies(R.complement(R.isEmpty), "expiryDate"),
      documents,
    ),
    ...startingDocs,
  ]

  const filteredDictionary: any = R.indexBy(R.prop("key"), filtered)

  return R.reduce(
    (acc: any, objCityDocs: any) => {
      const objFiltered = filteredDictionary[objCityDocs.key]

      if (objFiltered) {
        const newObjCityDocs = R.assoc(
          "expiryDate",
          objFiltered.expiryDate,
          objCityDocs,
        )
        return {
          ...acc,
          existing: [...acc.existing, newObjCityDocs],
        }
      }

      return {
        ...acc,
        nonExisting: [...acc.nonExisting, objCityDocs],
      }
    },
    { existing: [], nonExisting: [], others: otherDocsOrProcesses },
    filteredCityDocs,
  )
}

const getDocPrice = (key, category, docs, docType = "private", typeOfPlate) => {
  const selectedDoc = docs(docType, typeOfPlate).filter(
    (doc) => doc.key === key,
  )
  return {
    price: selectedDoc[0].price[category].price,
    cost: selectedDoc[0].price[category].cost,
  }
}

export const getInitialValues = (
  existing,
  category,
  processingCity,
  citiesData,
  typeOfPlate,
  typeOfDoc = "private",
) => {
  const initValues = {}

  if (existing.length) {
    existing.reduce((acc, val) => {
      if (
        daysToRenewal(val.expiryDate) < TWO_WEEKS_TO_EXPIRY &&
        !val.meta?.disabled // if a document is disabled, do not add it to the initial values
      ) {
        acc[val.key] = {
          price: val.price[category].price,
          cost: val.price[category].cost,
          qty:
            val.key === "vl" || val.key === "hp"
              ? calculatePaperQty(val.expiryDate)
              : 1,
          disabled: false,
          daysToRenewal: daysToRenewal(val.expiryDate),
        }
        if (val.connected.length) {
          val.connected.map((conn) => {
            const { price, cost } = getDocPrice(
              conn,
              category,
              citiesData[processingCity].docs,
              val.typeOfDoc,
              typeOfPlate,
            )
            return (acc[conn] = {
              price,
              cost,
              qty: 1,
              disabled: true,
            })
          })
        }
      }
      return acc
    }, initValues)
  }

  // Always add digital processing documents to initial values
  const cityDocs = citiesData[processingCity].docs(typeOfDoc, typeOfPlate)
  const processingDoc = cityDocs.find((doc) => doc.key === "processing")
  if (processingDoc && processingDoc.price[category].price > 0) {
    initValues.processing = {
      price: processingDoc.price[category].price,
      cost: processingDoc.price[category].cost,
      qty: 1,
      disabled: false,
      daysToRenewal: daysToRenewal(null),
    }
  }

  return initValues
}

export const mapPapers = (
  existing: any,
  category: number,
  calculatePaperQty: any,
  typeOfDoc: string,
  typeOfPlate: string,
  citiesData: any,
  processingCity: string,
  formik: any,
) =>
  existing.map((val: any, i: number) => {
    const selectedPrice = val?.price?.[category]

    return selectedPrice?.price > 0 ? (
      <Field
        key={i}
        name={val.key}
        color="primary"
        value={formik.values[val.key]}
        //   onChange={formik.handleChange}
        as={() => (
          <RadioBox
            disabled={formik.values[val.key]?.disabled || val.meta?.disabled}
            data-testid={`${val.key}-selector`}
            type="checkbox"
            subtitle={val.name}
            description={val?.description}
            descriptionStyles={{ fontSize: "0.75rem" }}
            selectProps={{
              id: val.name,
              checked: formik.values[val.key]?.price > 0 ? true : false,
              onChange: (e: any, value: any) => {
                formik.setFieldValue(
                  val.key,
                  value
                    ? {
                        price: selectedPrice.price,
                        cost: selectedPrice.cost,
                        qty:
                          val.key === "vl" || val.key === "hp"
                            ? calculatePaperQty(val.expiryDate)
                            : 1,
                        disabled: false,
                        daysToRenewal: daysToRenewal(val.expiryDate),
                      }
                    : { price: 0, cost: 0, qty: 1, disabled: false },
                )
                // if the papers have other papers connected, set their value once selected
                if (val.connected?.length) {
                  val.connected.map((con: any) => {
                    const { price, cost } = getDocPrice(
                      con,
                      category,
                      citiesData[processingCity].docs,
                      typeOfDoc,
                      typeOfPlate,
                    )
                    return formik.setFieldValue(
                      con,
                      value
                        ? {
                            price,
                            cost,
                            qty: 1,
                            disabled: true,
                          }
                        : {
                            price: 0,
                            cost: 0,
                            qty: 1,
                            disabled: false,
                          },
                    )
                  })
                }
              },
            }}
          >
            {val.expiryDate ? (
              <ColouredText
                paper={val}
                showRenewalQty={val.key === "vl" || val.key === "hp"}
              />
            ) : null}
          </RadioBox>
        )}
      />
    ) : null
  })

export const calculateTotal = (formik, connectedProcess: string) => {
  let total

  total = Object.values(formik.values).reduce((acc, val) => {
    if (R.has("price", val)) {
      acc += val.price * val.qty
    }
    return acc
  }, 0)

  if (!R.isEmpty(connectedProcess)) {
    Object.values(connectedProcess).forEach((process: any) => {
      if (process.data) {
        total += process.data?.fees[0].amount
      }
    })
  }

  return total
}

export const calculateProcessingTime = (docs, values) => {
  let processingTimes = []
  docs.forEach((doc) => {
    if (R.has(doc.key, values) && values[doc.key].price > 0) {
      processingTimes.push(doc.processingTime)
    }
  })
  processingTimes.sort((a, b) => b - a)
  return processingTimes[0]
}

export const listDelayedDocs = (values, processingCity) => {
  const delayedDocs = ["poc"]
  const delayedDocsList = []

  delayedDocs.forEach((docKey) => {
    if (
      R.has(docKey, values) &&
      values[docKey].price > 0 &&
      !notDeliveredDocs(processingCity).includes(docKey)
    ) {
      delayedDocsList.push(docKey)
    }
  })

  const allDelayedDocs = delayedDocsList.map((docKey: any, i: number) =>
    vehicleKeys(processingCity)[docKey] // if the docs have the key show it, else, dont. applicable in cases of drivers licence
      ? vehicleKeys(processingCity)[docKey]
      : "",
  )

  return allDelayedDocs.join(", ")
}

export const returnUploadsForNewVehicle = (val: any) => {
  return [
    {
      name: "Receipt",
      url: val?.customPapersorReceiptUpload?.url,
    },
    { name: "Id-card", url: val?.idCardUpload?.url },
    { name: "Chassis pic.", url: val?.chassisUpload?.url },
  ]
}

export const returnUploadsForOwnership = (val: any) => {
  return [
    { name: "Licence", url: val.vlUpload.url },
    { name: "POC", url: val.pocUpload.url },
    { name: "Transfer Doc", url: val.transferDocUpload.url },
    { name: "Receipt", url: val.receiptUpload.url },
    { name: "Photo", url: val.passportUpload.url },
    { name: "Id-card", url: val.idCardUpload.url },
    { name: "Chassis", url: val.chassisUpload.url },
  ]
}

export const returnUploadsForDriversLicense = (val: any) => {
  return [
    { name: "Licence", url: val.licenseUpload.url },
    { name: "Photo", url: val.passportPhotoUpload.url },
  ]
}

export const returnUploadsForComprehensiveInsurance = (val: any) => {
  return [
    { name: "Vehicle Licence", url: val.vlUpload.url },
    { name: "Front", url: val.frontUpload.url },
    { name: "Back", url: val.backUpload.url },
    { name: "Side One", url: val.sideOneUpload.url },
    { name: "Side Two", url: val.sideTwoUpload.url },
    { name: "Id-card", url: val.idCardUpload.url },
    {
      name: "Secondary Driver Id-card",
      url: val.secondaryDriverIdCardUpload.url,
    },
  ]
}

export const returnProcessingErrorText = (values: any) => {
  const { processingCity, hp, vehicle } = values

  if (
    processingCity?.value === "abuja" &&
    hp?.price > 0 &&
    (vehicle?.category === 2 || vehicle?.category === 3)
  ) {
    return "You cannot process Hackney Permit for a saloon car in Abuja, please remove Hackney Permit from your selection."
  }

  return null
}
